import React, { useCallback, useEffect, useState } from "react";
import {
  getInitiativePerformance,
  initiativeErrorMessage,
} from "../services/efficiencyInitiativesApi";

/**
 * Initiative Performance — rankings and outliers.
 *
 * Three presentation rules, each of which exists to stop a misleading reading:
 *
 *   * Initiatives whose windows lack data coverage are listed separately, never
 *     ranked. Ranking them as zero would place a project with no measurements
 *     below one that genuinely made things worse.
 *   * "No expectation set" is kept apart from "underperformed". Not being able to
 *     judge an initiative is not the same as judging it badly.
 *   * Ranking by loss reduction is offered but reports itself unavailable. The
 *     requirement exists; the underlying insight-loss comparison does not yet,
 *     and returning an all-zero ranking would look like a real result.
 */

// `col` marks which table column each ranking drives, so the active one can be
// highlighted. Without that, switching rankings looks like it does nothing
// whenever the resulting order happens to be unchanged — which is common, since
// financial savings are just energy x tariff and therefore almost always rank the
// same way.
const RANK_OPTIONS = [
  { key: "energy", label: "Energy savings", col: "energy", dir: "highest first" },
  { key: "financial", label: "Financial savings", col: "financial", dir: "highest first" },
  { key: "payback", label: "Payback period", col: "payback", dir: "shortest first" },
  { key: "loss", label: "Loss reduction", col: null, dir: null },
];

const PKR = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 });
const NUM = new Intl.NumberFormat("en-PK", { maximumFractionDigits: 1 });

const fmtKwh = (v) => (v == null ? "—" : PKR.format(v));
const fmtPkr = (v) => (v == null ? "—" : PKR.format(v));
const fmtPayback = (m) =>
  m == null ? "no payback" : m < 24 ? `${NUM.format(m)} mo` : `${NUM.format(m / 12)} yr`;

export default function InitiativePerformancePanel({ username, statusFilter }) {
  const [rankBy, setRankBy] = useState("energy");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getInitiativePerformance(username, { rankBy, status: statusFilter, top: 3 }));
    } catch (err) {
      setError(initiativeErrorMessage(err, "Could not rank initiatives."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [username, rankBy, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const topRefs = new Set((data?.top_performers || []).map((t) => t.reference_code));
  const underRefs = new Set((data?.underperformers || []).map((u) => u.reference_code));
  const activeOpt = RANK_OPTIONS.find((o) => o.key === rankBy);
  const activeCol = activeOpt?.col || null;

  return (
    <div className="ei-card">
      <div className="ei-impact-head">
        <h4>
          Initiative performance{" "}
          <span className="ei-impact-sub">
            measured savings, ranked · cancelled initiatives excluded
          </span>
        </h4>
      </div>

      {activeOpt?.col ? (
        <p className="ei-rank-note">
          Ordered by <strong>{activeOpt.label.toLowerCase()}</strong> ({activeOpt.dir}). Click
          any highlighted column heading to re-rank.
          {rankBy !== "payback" && (
            <> Energy and financial rankings usually match, because financial saving is
            energy multiplied by the tariff.</>
          )}
        </p>
      ) : (
        <p className="ei-rank-note">
          Click a column heading to rank by it.
        </p>
      )}

      {error && <div className="ei-alert error">{error}</div>}
      {loading && <p className="ei-hint">Computing measured impact for every initiative…</p>}

      {!loading && data && (
        <>
          {!data.ranking_available && (
            <div className="ei-alert info">
              <strong>Ranking by loss reduction is not available yet.</strong>
              <div style={{ marginTop: "0.3rem", fontWeight: 400 }}>
                {data.ranking_unavailable_reason}
              </div>
            </div>
          )}

          {data.truncated && (
            <div className="ei-alert info">
              Only the {data.count} most recent initiatives were ranked; older ones were
              excluded to keep this responsive.
            </div>
          )}

          {data.count === 0 ? (
            <div className="ei-empty">
              <strong>Nothing to rank</strong>
              No initiative yet has enough meter data on both sides of its
              implementation date.
            </div>
          ) : (
            <div className="ei-table-wrapper">
              <table className="ei-table">
                <thead>
                  <tr>
                    <th>{data.ranking_available ? "#" : ""}</th>
                    <th>ID</th>
                    <th>Initiative</th>
                    <th>Node</th>
                    {/* The headings ARE the ranking control — clicking one re-ranks.
                        Loss reduction is a heading too, so the requirement stays
                        visible even though the underlying comparison is not built. */}
                    <SortableTh
                      label="Energy (kWh/yr)" rankKey="energy"
                      rankBy={rankBy} onSort={setRankBy} arrow="↓"
                    />
                    <SortableTh
                      label="Financial (PKR/yr)" rankKey="financial"
                      rankBy={rankBy} onSort={setRankBy} arrow="↓"
                    />
                    <SortableTh
                      label="Payback" rankKey="payback"
                      rankBy={rankBy} onSort={setRankBy} arrow="↑"
                    />
                    <SortableTh
                      label="Loss reduction" rankKey="loss"
                      rankBy={rankBy} onSort={setRankBy} arrow="↓"
                      title="Needs the before/after insight-loss comparison"
                    />
                    <th className="ei-num">vs expected</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ranked.map((e) => {
                    const attain = e.expected_vs_actual?.kwh_attainment_percent;
                    const isTop = topRefs.has(e.reference_code);
                    const isUnder = underRefs.has(e.reference_code);
                    return (
                      <tr key={e.initiative_id} className="ei-row">
                        <td className="ei-rank">
                          {e.rank != null ? e.rank : "—"}
                          {isTop && <span className="ei-star" title="Top performer">★</span>}
                        </td>
                        <td className="ei-ref">{e.reference_code}</td>
                        <td>
                          <div className="ei-title-cell">{e.title}</div>
                        </td>
                        <td>
                          <span className={`ei-badge node-${e.node_type}`}>{e.node_type}</span>
                          <div className="ei-title-sub">{e.node_name}</div>
                        </td>
                        <td className={`ei-num${activeCol === "energy" ? " ei-col-active" : ""}`}>
                          {fmtKwh(e.energy?.annualised_saving_kwh)}
                        </td>
                        <td className={`ei-num${activeCol === "financial" ? " ei-col-active" : ""}`}>
                          {fmtPkr(e.financial?.annualised_saving_pkr)}
                        </td>
                        <td className={`ei-num${activeCol === "payback" ? " ei-col-active" : ""}`}>
                          {fmtPayback(e.financial?.simple_payback_months)}
                        </td>
                        <td className={`ei-num${activeCol === "loss" ? " ei-col-active" : ""}`}>
                          {e.loss_reduction == null ? (
                            <span className="ei-hint">n/a</span>
                          ) : (
                            fmtKwh(e.loss_reduction)
                          )}
                        </td>
                        <td className="ei-num">
                          {attain == null ? (
                            <span className="ei-hint">not set</span>
                          ) : (
                            <strong className={isUnder ? "ei-bad" : "ei-good"}>
                              {NUM.format(attain)}%
                            </strong>
                          )}
                        </td>
                        <td>
                          <span className={`ei-badge status-${e.status}`}>{e.status}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="ei-impact-cols" style={{ marginTop: "1.5rem" }}>
            <div>
              {/* Overall, NOT "by whichever column is selected". A leaderboard whose
                  meaning changes every time a heading is clicked is not a
                  leaderboard. This list is stable across the ranking toggle. */}
              <p className="ei-detail-label">Highest performing overall</p>
              {data.overall_top_performers?.length ? (
                <>
                  <ol className="ei-rank-list">
                    {data.overall_top_performers.map((t) => (
                      <li key={t.initiative_id}>
                        <strong>{t.reference_code}</strong> {t.title}
                        <div className="ei-title-sub">
                          {fmtKwh(t.energy?.annualised_saving_kwh)} kWh/yr ·{" "}
                          {fmtPkr(t.financial?.annualised_saving_pkr)} PKR/yr ·{" "}
                          {fmtPayback(t.financial?.simple_payback_months)}
                        </div>
                        {t.overall_positions && (
                          <div className="ei-title-sub">
                            avg rank {t.overall_average_rank} — energy #
                            {t.overall_positions.energy}, financial #
                            {t.overall_positions.financial}, payback #
                            {t.overall_positions.payback}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  <p className="ei-hint" style={{ marginTop: "0.5rem" }}>
                    {data.overall_method}. {data.overall_caveat}.
                  </p>
                </>
              ) : (
                <p className="ei-hint">Nothing measurable to rank yet.</p>
              )}
            </div>

            <div>
              <p className="ei-detail-label">
                Below expected performance (under{" "}
                {data.underperform_threshold_percent}% of their own target)
              </p>
              {data.underperformers.length === 0 ? (
                <p className="ei-hint">
                  None — every measurable initiative is meeting or beating its expected
                  saving.
                </p>
              ) : (
                <ul className="ei-rank-list">
                  {data.underperformers.map((u) => (
                    <li key={u.initiative_id}>
                      <strong>{u.reference_code}</strong> {u.title}
                      <div className="ei-title-sub ei-bad">
                        {NUM.format(u.expected_vs_actual.kwh_attainment_percent)}% of
                        expected — {fmtKwh(u.energy?.annualised_saving_kwh)} kWh/yr against a
                        target of {fmtKwh(u.expected_vs_actual.expected_annual_kwh)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {data.no_expectation_set.length > 0 && (
                <p className="ei-hint" style={{ marginTop: "0.6rem" }}>
                  Cannot be judged (no expected saving recorded):{" "}
                  {data.no_expectation_set.join(", ")}
                </p>
              )}
            </div>
          </div>

          {data.not_measurable.length > 0 && (
            <div style={{ marginTop: "1.25rem" }}>
              <p className="ei-detail-label">Excluded — not yet measurable</p>
              <ul className="ei-rank-list">
                {data.not_measurable.map((n) => (
                  <li key={n.initiative_id}>
                    <strong>{n.reference_code}</strong> {n.title}
                    <div className="ei-title-sub">{n.reason}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/**
 * A column heading that doubles as the ranking control.
 *
 * Kept as a real <button> inside the <th> rather than an onClick on the th, so it
 * is reachable by keyboard and announced as actionable — a heading that silently
 * responds to clicks is invisible to anyone not using a mouse.
 */
function SortableTh({ label, rankKey, rankBy, onSort, arrow, title }) {
  const active = rankBy === rankKey;
  return (
    <th className={`ei-num ei-sortable${active ? " ei-col-active" : ""}`} title={title}>
      <button
        type="button"
        className="ei-sort-btn"
        onClick={() => onSort(rankKey)}
        aria-pressed={active}
      >
        {label}
        <span className="ei-sort-arrow">{active ? arrow : ""}</span>
      </button>
    </th>
  );
}
